import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { VehicleSocket } from "../../../../../store/admin/vehiclesSocketStore";
import {
  useVehicleFootprintTrackingStore,
  useIsFootprintActive,
  useFootprintPointsCount,
} from "../../../../../store/admin/vehicleFootprintTrackingStore";
import { useAlert } from "../../../../../context/AlertContext";
import { useRoutesStore } from "../../../../../store/admin/routesStore";
import useIsMonitor from "../../../../../hooks/useIsMonitor";
import { useRouteForm } from "../../../../admin/components/sections/routesComponents";
import { RouteModal } from "../../../../admin/components/sections/routesComponents";
import type { ThemeClasses, RouteFormValues } from "../../../../admin/components/sections/routesComponents/types";

interface FootprintContentProps {
  vehicle: VehicleSocket;
  isDarkMode: boolean;
}

export default function FootprintContent({
  vehicle,
  isDarkMode,
}: FootprintContentProps) {
  const { startFootprint, stopFootprint, setFootprintColor, getFootprint } =
    useVehicleFootprintTrackingStore();

  const isCurrentlyTracking = useIsFootprintActive(vehicle.id);
  const footprintPointsCount = useFootprintPointsCount(vehicle.id);

  const [enabled, setEnabled] = useState(isCurrentlyTracking);
  const [selectedColor, setSelectedColor] = useState("#F59E0B");
  const [showRouteModal, setShowRouteModal] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const { t } = useTranslation("lite");
  const { showAlert } = useAlert();
  const { isMonitor } = useIsMonitor();
  const createRoute = useRoutesStore((state) => state.createRoute);
  const borderColor = isDarkMode ? "border-gray-700" : "border-gray-200";

  const themeClasses: ThemeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  const {
    form,
    routePoints,
    routeDistance,
    estimatedTime,
    hasRealChanges,
    handleMapPointsChange,
    handleCoordinateEdit,
    handleAddressEdit,
    handleAddPoint,
    handleRemovePoint,
    handleRouteCalculated,
    handleAxisCalculated,
    handleBufferChange,
    resetForm,
    prepareRouteData,
  } = useRouteForm({ isEdit: false, editRoute: null });

  useEffect(() => {
    setEnabled(isCurrentlyTracking);
    const footprint = getFootprint(vehicle.id);
    if (footprint) {
      setSelectedColor(footprint.color);
    }
  }, [vehicle.id, isCurrentlyTracking, getFootprint]);

  const handleToggle = () => {
    if (enabled) {
      stopFootprint(vehicle.id);
      setEnabled(false);
    } else {
      startFootprint(
        vehicle.id,
        vehicle.vehicle.name || vehicle.fullId,
        selectedColor,
        vehicle.latitude,
        vehicle.longitude
      );
      setEnabled(true);
    }
  };

  const colorDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleColorChange = (newColor: string) => {
    setSelectedColor(newColor);

    if (enabled) {
      if (colorDebounceRef.current) {
        clearTimeout(colorDebounceRef.current);
      }
      colorDebounceRef.current = setTimeout(() => {
        setFootprintColor(vehicle.id, newColor);
      }, 300);
    }
  };

  useEffect(() => {
    return () => {
      if (colorDebounceRef.current) {
        clearTimeout(colorDebounceRef.current);
      }
    };
  }, []);

  const handleOpenRouteModal = useCallback(() => {
    const footprint = getFootprint(vehicle.id);
    if (!footprint || footprint.points.length < 2) return;

    handleMapPointsChange(
      footprint.points.map((p) => ({ lat: p.lat, lng: p.lng }))
    );
    setShowRouteModal(true);
  }, [vehicle.id, getFootprint, handleMapPointsChange]);

  const handleCloseRouteModal = useCallback(() => {
    setShowRouteModal(false);
    setLoadingSubmit(false);
    resetForm();
  }, [resetForm]);

  const handleCreateRouteSubmit = useCallback(
    async (data: RouteFormValues) => {
      setLoadingSubmit(true);
      const companyId = localStorage.getItem("companyId");
      const payload = {
        ...prepareRouteData(data),
        company_id: companyId || "",
      };

      const result = await createRoute(payload);

      if (result.success) {
        showAlert({
          title: t("footprintContent.routeCreatedTitle", { defaultValue: "Ruta creada" }),
          text: t("footprintContent.routeCreatedText", { defaultValue: "Ruta creada correctamente" }),
          icon: "success",
        });
        handleCloseRouteModal();
      } else {
        showAlert({
          title: t("footprintContent.routeErrorTitle", { defaultValue: "Error" }),
          text: result.error || t("footprintContent.routeErrorText", { defaultValue: "Error al crear la ruta" }),
          icon: "error",
        });
      }
      setLoadingSubmit(false);
    },
    [prepareRouteData, createRoute, showAlert, t, handleCloseRouteModal]
  );

  return (
    <div className={`p-3 border-t ${borderColor}`}>
      <h4 className="text-primary-500 font-semibold text-xs mb-2">
        {t("footprintContent.title")}
      </h4>

      <div className="flex items-center gap-3">
        <span
          className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
        >
          {t("footprintContent.activate")}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={handleToggle}
          className={`
            relative inline-flex h-5 w-10 items-center rounded-full transition-colors duration-200
            ${enabled ? "bg-primary-500" : "bg-gray-300"}
          `}
        >
          <span
            className={`
              inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform duration-200
              ${enabled ? "translate-x-5" : "translate-x-1"}
            `}
          />
        </button>
      </div>

      {enabled && (
        <>
          <div className="mt-4">
            <h5
              className={`font-medium text-xs mb-2 ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              {t("footprintContent.footprintColor", { defaultValue: "Color de huella" })}
            </h5>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={selectedColor}
                onChange={(e) => handleColorChange(e.target.value)}
                className="w-10 h-10 rounded cursor-pointer border-0 p-0"
              />
              <span
                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {selectedColor}
              </span>
            </div>
          </div>

          <div className="mt-3">
            <p
              className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              {t("footprintContent.pointsRecorded", { defaultValue: "Puntos registrados" })}: {footprintPointsCount}
            </p>
          </div>

          {!isMonitor && (
            <button
              onClick={handleOpenRouteModal}
              disabled={footprintPointsCount < 2}
              className={`w-full px-3 py-2 rounded text-[10px] font-medium bg-primary-500 text-black mt-2 transition-colors ${
                footprintPointsCount < 2
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-primary-400"
              }`}
            >
              {t("footprintContent.createRoute", { defaultValue: "Crear ruta con estos puntos" })}
            </button>
          )}
          {/* El aviso es del botón: sin botón (monitor) no tiene a qué referirse. */}
          {!isMonitor && footprintPointsCount < 2 && (
            <p
              className={`text-[10px] mt-1 ${
                isDarkMode ? "text-gray-500" : "text-gray-400"
              }`}
            >
              {t("footprintContent.needAtLeast2Points", { defaultValue: "Se necesitan al menos 2 puntos" })}
            </p>
          )}
        </>
      )}

      <RouteModal
        isOpen={showRouteModal}
        isEdit={false}
        loadingSubmit={loadingSubmit}
        form={form}
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        routePoints={routePoints}
        routeDistance={routeDistance}
        estimatedTime={estimatedTime}
        hasRealChanges={hasRealChanges}
        onCoordinateEdit={handleCoordinateEdit}
        onAddressEdit={handleAddressEdit}
        onAddPoint={handleAddPoint}
        onRemovePoint={handleRemovePoint}
        onMapPointsChange={handleMapPointsChange}
        onRouteCalculated={handleRouteCalculated}
        onAxisCalculated={handleAxisCalculated}
        onCorridorBufferChange={handleBufferChange}
        onSubmit={handleCreateRouteSubmit}
        onClose={handleCloseRouteModal}
      />
    </div>
  );
}
